import { Button, Tabs } from 'antd';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scrollbars } from 'react-custom-scrollbars-4';
import type { Control, FieldValues } from 'react-hook-form';

import Field from '~/shared/components/field';
import FormNativeIdContext from '~/shared/components/form-block/form-native-id-context';
import { SidebarActionsPortalContext } from '~/shared/components/sidebar/sidebar';
import type { FormButtonType, FormFieldType, FormSection } from '~/shared/types/form-type';

import '~/shared/components/form-block/patterns/basic-form-pattern.scss';

type ButtonType = FormButtonType<FieldValues>;
type PositionType = FormSection<FieldValues>;

export type BasicFormPatternPropsType = {
  submitting?: boolean;
  isFixedActions?: boolean;
  isScrollBar?: boolean;
  textAlign?: string;
  control: Control<FieldValues, unknown>;
  errors: Record<string, unknown>;
  getValues: (fieldName?: string) => unknown;
  setValue: (fieldName: string, value: unknown) => void;
  position1?: PositionType;
  position2?: PositionType;
  position3?: PositionType;
  position4?: PositionType;
  navigation?: PositionType;
  settings?: PositionType;
  actions?: {
    buttons: readonly ButtonType[];
  };
  t: (key: string, options: { ns: string[] }) => string;
  watch: (fieldName?: string) => unknown;
  fieldConfig?: { name?: string };
  append?: unknown;
  tabs?: readonly { key: string; label: string; disabled?: boolean }[];
  isFooterReverse?: boolean;
};

const renderButton = ({
  button,
  key,
  getValues,
  setValue,
  watch,
  append,
  formId,
}: {
  button: ButtonType;
  key: React.Key;
  getValues: (fieldName?: string) => unknown;
  setValue: (fieldName: string, value: unknown) => void;
  watch: (fieldName?: string) => unknown;
  append?: unknown;
  formId?: string;
}) => {
  if (button?.render) {
    const node = button.render({
      getValues: getValues as never,
      setValue: setValue as never,
      watch: watch as never,
      append,
      disabled: button?.disabled,
    });

    if (React.isValidElement(node)) {
      const nodeHtmlType = (node.props as { htmlType?: string })?.htmlType;
      const isSubmit = button?.htmlType === 'submit' || nodeHtmlType === 'submit';

      return React.cloneElement(node as React.ReactElement<any>, {
        key,
        ...(isSubmit && formId ? { form: formId } : {}),
      });
    }

    return <React.Fragment key={key}>{node}</React.Fragment>;
  }

  return (
    <Button
      key={key}
      disabled={button?.disabled}
      htmlType={button?.htmlType}
      form={button?.htmlType === 'submit' ? formId : undefined}
      size={button?.size ?? 'middle'}
      className={button?.className}
      onClick={
        button?.onClick
          ? () => button.onClick?.({ getValues: getValues as never, setValue: setValue as never })
          : undefined
      }
    >
      {button?.buttonName}
    </Button>
  );
};

const renderSectionFields = ({
  section,
  fieldConfig,
  watch,
  getValues,
  errors,
  setValue,
  control,
}: {
  section?: PositionType;
  fieldConfig?: { name?: string };
  watch: (fieldName?: string) => unknown;
  getValues: (fieldName?: string) => unknown;
  errors: Record<string, unknown>;
  setValue: (fieldName: string, value: unknown) => void;
  control: Control<FieldValues>;
}) => {
  return (section?.formFields ?? [])
    .filter((formField: FormFieldType) => formField?.config?.checkIsFieldVisible?.(watch as never) ?? true)
    .map((formField: FormFieldType) => (
      <Field
        key={formField.name}
        fieldConfig={fieldConfig}
        errors={errors}
        watch={watch}
        className={formField.className}
        getValues={getValues}
        setValue={setValue}
        control={control}
        formField={formField}
      />
    ));
};

export const BasicFormPattern = memo((props: BasicFormPatternPropsType) => {
  const {
    position1,
    position2,
    position3,
    position4,
    settings,
    navigation,
    actions,
    tabs,
    t,
    append,
    errors,
    fieldConfig,
    setValue,
    getValues,
    isScrollBar = false,
    isFixedActions = false,
    watch,
    control,
    isFooterReverse,
  } = props;

  const [tab, setTab] = useState<string | null>(tabs?.[0]?.key ?? null);
  const sidebarActionsPortalTarget = useContext(SidebarActionsPortalContext);
  const nativeFormId = useContext(FormNativeIdContext);

  const position4Fields = useMemo(
    () =>
      renderSectionFields({
        section: position4,
        fieldConfig,
        watch,
        getValues,
        errors,
        setValue,
        control,
      }),
    [control, errors, fieldConfig, getValues, position4, setValue, watch],
  );

  const settingsFields = useMemo(
    () =>
      renderSectionFields({
        section: settings,
        fieldConfig,
        watch,
        getValues,
        errors,
        setValue,
        control,
      }),
    [control, errors, fieldConfig, getValues, setValue, settings, watch],
  );

  const formContent = (
    <div className="basic-form-pattern__form-content">
      {tabs && (
        <Tabs
          onChange={setTab}
          activeKey={tab ?? undefined}
          type="card"
          items={tabs.map((item) => ({
            key: item.key,
            disabled: item.disabled ?? false,
            label: (
              <div className="online-courses__tab">
                <span>{item.label}</span>
              </div>
            ),
          }))}
        />
      )}

      <div
        className={classNames('basic-form-pattern__description', {
          'basic-form-pattern--hidden': Boolean(tabs && tab !== 'description'),
        })}
      >
        {(position1 || position2 || position3) && (
          <div className="basic-form-pattern__position-block-1">
            {position1 && (
              <div className="basic-form-pattern__position-1">
                {position1.title && <div className="basic-form-pattern__title">{position1.title}</div>}
                {renderSectionFields({ section: position1, fieldConfig, watch, getValues, errors, setValue, control })}
                {(position1.buttons ?? []).map((button, index) =>
                  renderButton({ button, key: index, getValues, watch, setValue, append }),
                )}
              </div>
            )}

            <div className="basic-form-pattern__position-block-2">
              {position2 && (
                <div className="basic-form-pattern__position-2">
                  {position2.title && <div className="basic-form-pattern__title">{position2.title}</div>}
                  {renderSectionFields({
                    section: position2,
                    fieldConfig,
                    watch,
                    getValues,
                    errors,
                    setValue,
                    control,
                  })}
                  {(position2.buttons ?? []).map((button, index) =>
                    renderButton({ button, key: index, getValues, watch, setValue, append }),
                  )}
                </div>
              )}

              {position3 && (
                <div className="basic-form-pattern__position-3">
                  {renderSectionFields({
                    section: position3,
                    fieldConfig,
                    watch,
                    getValues,
                    errors,
                    setValue,
                    control,
                  })}
                  {(position3.buttons ?? []).map((button, index) =>
                    renderButton({ button, key: index, getValues, watch, setValue, append }),
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {position4 && (
          <div className="basic-form-pattern__position-4">
            {position4.title && <div className="basic-form-pattern__title">{position4.title}</div>}
            {position4Fields}
            {(position4.buttons ?? []).map((button, index) =>
              renderButton({ button, key: index, getValues, watch, setValue, append }),
            )}
          </div>
        )}
      </div>

      <div
        className={classNames('basic-form-pattern__navigation', {
          'basic-form-pattern--hidden': Boolean(tabs && tab !== 'navigation'),
        })}
      >
        {renderSectionFields({ section: navigation, fieldConfig, watch, getValues, errors, setValue, control })}
        {(navigation?.buttons ?? []).map((button, index) =>
          renderButton({ button, key: index, getValues, watch, setValue, append }),
        )}
      </div>

      <div
        className={classNames('basic-form-pattern__settings', {
          'basic-form-pattern--hidden': Boolean(tabs && tab !== 'settings'),
        })}
      >
        {settings && (
          <div className="basic-form-pattern__position-4">
            {settings.formFields.length > 0 && t('text.settings')}
            {settingsFields}
            {(settings.buttons ?? []).map((button, index) =>
              renderButton({ button, key: index, getValues, watch, setValue, append }),
            )}
          </div>
        )}
      </div>
    </div>
  );

  const actionsNode = actions ? (
    <div
      className={classNames('basic-form-pattern__actions', {
        'basic-form-pattern__actions--fixed': isFixedActions,
        'basic-form-pattern__actions--portal': Boolean(sidebarActionsPortalTarget),
      })}
    >
      {actions.buttons.map((button, index) =>
        renderButton({
          button,
          key: index,
          getValues,
          watch,
          setValue,
          append,
          formId: nativeFormId ?? undefined,
        }),
      )}
    </div>
  ) : null;

  return (
    <div
      className={classNames('basic-form-pattern', {
        'basic-form-pattern--footer-reverse': isFooterReverse,
      })}
    >
      <div className="basic-form-pattern__content">
        {isScrollBar ? <Scrollbars universal>{formContent}</Scrollbars> : formContent}
      </div>

      {actionsNode &&
        (sidebarActionsPortalTarget ? createPortal(actionsNode, sidebarActionsPortalTarget) : actionsNode)}
    </div>
  );
});
